import React from 'react';
import { clientUrls } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { useSelectCurrentOrder } from '../../../selectors/order/selectCurrentOrder';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';
import { Text, Tabs, ThemeConstellation, Flex } from '@zillow/constellation';
import { ThemeProvider } from 'styled-components';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'store/store';
import { OrderTabs } from 'store/order/orderNavigation/orderNavigationSlice';
import { QuickTasks } from './QuickTasks';
import { QuickComment } from './QuickComment';
import { QuickContact } from './QuickContact';

export const OrderNavigation: React.FC<{
  orderId: string;
}> = ({ orderId }) => {
  const { data: order } = useSelectCurrentOrder();

  const isOrderWorkflowEnabled = useHasFeatureFlag('order.workflow');
  const isMessagingEnabled = useHasFeatureFlag('messaging.ui');

  const { tab } = useAppSelector((rootStore) => rootStore.order.orderNavigationSlice);

  const orderTabs = [
    { id: OrderTabs.Overview, link: clientUrls.urlFor.orderStatus(orderId) },
    { id: OrderTabs.Contacts, link: clientUrls.urlFor.orderContacts(orderId) },
    // Extrnal Data is on hiatus for now. It might be back in 2025
    // { id: OrderTabs.ExternalData, link: clientUrls.urlFor.orderExternalData(orderId) },
    { id: OrderTabs.Comments, link: clientUrls.urlFor.adminCommentsInternal(orderId) },
    { id: OrderTabs.Tasks, link: clientUrls.urlFor.orderTasks(orderId) },
    { id: OrderTabs.Documents, link: clientUrls.urlFor.documentsIndex(orderId) },
    { id: OrderTabs.Title, link: clientUrls.urlFor.title(orderId) },
    { id: OrderTabs.Scheduling, link: clientUrls.urlFor.orderScheduling(orderId) },
    {
      id: OrderTabs.Fees,
      link: order.fees_version === 'v1' ? clientUrls.urlFor.cdItemsV3(orderId) : clientUrls.urlFor.feesTab(orderId),
    },
    order.fees_version === 'v1' && { id: OrderTabs.Escrow, link: clientUrls.urlFor.escrowSPA(orderId) },
    { id: OrderTabs.Activity, link: clientUrls.urlFor.activity(orderId) },
    isOrderWorkflowEnabled && { id: OrderTabs.Workflow, link: clientUrls.urlFor.workflows(orderId) },
    isMessagingEnabled && { id: OrderTabs.Inbox, link: clientUrls.urlFor.inbox(orderId) },
  ]
    .filter((t) => !!t)
    .map((t) => t as { id: string; link: string });

  return (
    <div data-test-id={'order-navigation'}>
      <ThemeProvider theme={ThemeConstellation as any}>
        <Flex display={'flex'} gap={1}>
          <Tabs>
            <Tabs.List aria-label="Order Navigation">
              {orderTabs.map((t) => (
                <Tabs.Tab
                  key={t.id}
                  id={t.id}
                  as={Link}
                  to={t.link}
                  aria-selected={tab === t.id}
                  className={'!pb-2 !pt-1'} // Needs ! which means important because Styled Components are more specific than tailwind css
                >
                  <Text>{t.id}</Text>
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
          <QuickTasks />
          <QuickComment />
          <QuickContact />
        </Flex>
      </ThemeProvider>
    </div>
  );
};
